
import React, { useMemo } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { CreditCard, X, FileText } from "lucide-react";
import { Invoice } from "@/types/payments";

interface BulkActionsProps {
  selectedInvoices: string[];
  handleBulkPayment: () => void;
  handleBulkCancel: () => void;
  handleBulkExport?: () => void;
  handlePayAll?: () => void;
  activeTab: string;
  showPayAll?: boolean;
  allInvoices?: Invoice[];
}

const BulkActions = ({
  selectedInvoices,
  handleBulkPayment,
  handleBulkCancel,
  handleBulkExport,
  handlePayAll,
  activeTab,
  showPayAll = false,
  allInvoices = [],
}: BulkActionsProps) => {
  const t = useTranslations("consultantPayables.bulk");
  // Check if there are mixed invoice types (paid/pending with unpaid/overdue)
  const hasMixedSelection = useMemo(() => {
    if (selectedInvoices.length === 0 || !allInvoices.length) return false;
    
    const selectedInvoicesData = allInvoices.filter(inv => selectedInvoices.includes(inv.id));
    const hasUnpaidOrOverdue = selectedInvoicesData.some(inv => inv.status === "unpaid" || inv.status === "overdue");
    const hasPaidOrPending = selectedInvoicesData.some(inv => inv.status === "paid" || inv.status === "manually_handled" || inv.status === "pending");
    
    return hasUnpaidOrOverdue && hasPaidOrPending;
  }, [selectedInvoices, allInvoices]);
  
  // Count unpaid/overdue invoices in the selection
  const unpaidSelectedCount = useMemo(() => {
    return allInvoices
      .filter(inv => selectedInvoices.includes(inv.id))
      .filter(inv => inv.status === "unpaid" || inv.status === "overdue")
      .length;
  }, [selectedInvoices, allInvoices]);

  // Don't show bulk actions for "all" tab without selection or if no selection in other tabs
  if (selectedInvoices.length === 0 && (!showPayAll || activeTab === "all")) {
    return null;
  }

  // Render different bulk actions based on active tab
  const renderBulkAction = () => {
    switch (activeTab) {
      case "unpaid":
      case "overdue":
        return (
          <>
            {selectedInvoices.length > 0 ? (
              // Show "Pay Selected" when invoices are selected
              <Button 
                variant="default" 
                size="sm" 
                onClick={handleBulkPayment}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {t("paySelected", { count: String(selectedInvoices.length) })}
              </Button>
            ) : (
              // Show "Pay All" when no invoices are selected but showPayAll is true
              showPayAll && handlePayAll && (
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={handlePayAll}
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t("payAll")}
                </Button>
              )
            )}
          </>
        );
      case "pending":
        return (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleBulkCancel}
          >
            <X className="mr-2 h-4 w-4" />
            {t("cancelSelected", { count: String(selectedInvoices.length) })}
          </Button>
        );
      case "paid":
        return (
          <Button 
            variant="default" 
            size="sm"
            onClick={handleBulkExport}
          >
            <FileText className="mr-2 h-4 w-4" />
            {t("exportSelected", { count: String(selectedInvoices.length) })}
          </Button>
        );
      case "all":
        if (hasMixedSelection && unpaidSelectedCount > 0) {
          return (
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleBulkPayment}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {t("payUnpaid", { count: String(unpaidSelectedCount) })}
            </Button>
          );
        } else if (unpaidSelectedCount > 0) {
          return (
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleBulkPayment}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {t("paySelected", { count: String(selectedInvoices.length) })}
            </Button>
          );
        } else if (selectedInvoices.length > 0) {
          // If only paid/manually handled/pending invoices are selected
          return (
            <Button 
              variant="default" 
              size="sm"
              onClick={handleBulkExport}
            >
              <FileText className="mr-2 h-4 w-4" />
              {t("exportSelected", { count: String(selectedInvoices.length) })}
            </Button>
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="flex gap-2">
      {renderBulkAction()}
    </div>
  );
};

export default BulkActions;
